import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, BookOpen, Video, Clock, Calendar, ExternalLink, FileText, Download, X } from 'lucide-react';
import { getBatchClassById, getBatchStudyMaterials } from '../../firebase/collections';
import VideoPlayer from '../../components/VideoPlayer';
import InbuiltPDFViewer from '../../components/InbuiltPDFViewer';
import toast from 'react-hot-toast';

const BatchClassView = () => {
  const { classId } = useParams();
  const navigate = useNavigate();
  const [classData, setClassData] = useState(null);
  const [studyMaterials, setStudyMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMaterials, setShowMaterials] = useState(false);
  const [pdfViewerState, setPdfViewerState] = useState({ open: false, url: null, title: null });

  useEffect(() => {
    loadClassData();
  }, [classId]);

  const loadClassData = async () => {
    try {
      setLoading(true);
      const classResult = await getBatchClassById(classId);
      
      if (classResult.success) {
        setClassData(classResult.data);
        
        // Load study materials
        const materialsResult = await getBatchStudyMaterials(classId);
        if (materialsResult.success) {
          setStudyMaterials(materialsResult.data || []);
        } else {
          // Silently handle error - materials just won't show
          setStudyMaterials([]);
        }
      } else {
        toast.error('Class not found');
        navigate(-1);
      }
    } catch (error) {
      toast.error('Failed to load class');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (time) => {
    if (!time) return '';
    if (typeof time === 'string') {
      return time;
    }
    const date = time instanceof Date ? time : new Date(time);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString('en-US', { 
      day: 'numeric', 
      month: 'long',
      year: 'numeric'
    });
  };

  const getClassStatus = () => {
    if (!classData) return 'scheduled';
    
    // Handle Firestore Timestamp
    let classDate;
    if (classData.date?.toDate) {
      classDate = classData.date.toDate();
    } else if (classData.date instanceof Date) {
      classDate = classData.date;
    } else if (typeof classData.date === 'string') {
      classDate = new Date(classData.date);
    } else {
      return 'scheduled';
    }

    const now = new Date();
    const timeStr = classData.time || '00:00';
    const [hours, minutes] = timeStr.split(':').map(Number);
    const classDateTime = new Date(classDate);
    classDateTime.setHours(hours, minutes || 0, 0, 0);
    const classEndTime = new Date(classDateTime);
    classEndTime.setMinutes(classEndTime.getMinutes() + (classData.duration || 60));

    if (classData.status === 'completed' || classData.recordingUrl) {
      return 'completed';
    } else if (classData.status === 'live' || (now >= classDateTime && now <= classEndTime)) {
      return 'live';
    } else {
      return 'scheduled';
    }
  };

  const handleJoinClass = () => {
    if (classData?.zoomLink) {
      window.open(classData.zoomLink, '_blank');
    } else {
      toast.error('Zoom link not available');
    }
  };

  // Check if URL is a PDF
  const isPDF = (url) => {
    if (!url) return false;
    const urlLower = url.toLowerCase();
    return urlLower.includes('.pdf') || urlLower.includes('application/pdf') || urlLower.includes('content-type=application/pdf');
  };

  // Handle material click
  const handleMaterialClick = (e, material) => {
    if (!material.url) {
      e.preventDefault();
      toast.error('Material URL not available');
      return;
    }

    // If it's a PDF, open in InbuiltPDFViewer
    if (isPDF(material.url)) {
      e.preventDefault();
      setPdfViewerState({
        open: true,
        url: material.url,
        title: material.title || material.name || 'Study Material'
      });
    }
    // For non-PDF files, let the default link behavior happen (opens in new tab)
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!classData) {
    return null;
  }

  const status = getClassStatus();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Schedule</span>
          </button>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  {classData.subject}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(classData.date)}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Clock className="w-4 h-4" />
                    <span>{formatTime(classData.time)}</span>
                  </div>
                  {classData.duration && (
                    <div className="flex items-center space-x-2">
                      <Video className="w-4 h-4" />
                      <span>{classData.duration} minutes</span>
                    </div>
                  )}
                </div>
                {classData.isRevision && (
                  <div className="mt-2">
                    <span className="inline-block px-3 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded-full text-sm font-medium">
                      Revision Class
                    </span>
                  </div>
                )}
              </div>

              {/* Book Button - Always show if materials exist */}
              {studyMaterials.length > 0 ? (
                <motion.button
                  onClick={() => setShowMaterials(!showMaterials)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                    showMaterials 
                      ? 'bg-green-600 hover:bg-green-700 text-white' 
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <BookOpen className="w-5 h-5" />
                  <span>Study Materials ({studyMaterials.length})</span>
                </motion.button>
              ) : (
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  No study materials available
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className={`grid gap-6 ${showMaterials && studyMaterials.length > 0 ? 'grid-cols-1 lg:grid-cols-3' : 'grid-cols-1'}`}>
          {/* Main Content */}
          <div className={showMaterials && studyMaterials.length > 0 ? 'lg:col-span-2 space-y-6' : 'space-y-6'}>
            {/* Class Status Card */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              {status === 'live' && (
                <div className="text-center py-8">
                  <div className="inline-flex items-center space-x-2 px-6 py-3 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full mb-4">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="font-semibold">Class is Live Now</span>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Click the button below to join the live class on Zoom
                  </p>
                  <motion.button
                    onClick={handleJoinClass}
                    className="inline-flex items-center space-x-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <ExternalLink className="w-5 h-5" />
                    <span>Join Zoom Meeting</span>
                  </motion.button>
                </div>
              )}

              {status === 'scheduled' && (
                <div className="text-center py-8">
                  <div className="inline-flex items-center space-x-2 px-6 py-3 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 rounded-full mb-4">
                    <Clock className="w-5 h-5" />
                    <span className="font-semibold">This Content is Scheduled</span>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400">
                    This class will be available on {formatDate(classData.date)} at {formatTime(classData.time)}}
                  </p>
                </div>
              )}

              {status === 'completed' && classData.recordingUrl && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Class Recording
                  </h3>
                  <div className="rounded-lg overflow-hidden">
                    <VideoPlayer
                      src={classData.recordingUrl}
                      title={classData.subject}
                      className="aspect-video"
                    />
                  </div>
                  {classData.description && (
                    <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                      <p className="text-gray-700 dark:text-gray-300">{classData.description}</p>
                    </div>
                  )}
                </div>
              )}

              {status === 'completed' && !classData.recordingUrl && (
                <div className="text-center py-8">
                  <p className="text-gray-600 dark:text-gray-400">
                    This class has been completed. Recording will be available soon.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar - Study Materials */}
          {showMaterials && studyMaterials.length > 0 && (
            <div className="lg:col-span-1">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 sticky top-4 max-h-[calc(100vh-8rem)] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                    <BookOpen className="w-5 h-5" />
                    <span>Study Materials ({studyMaterials.length})</span>
                  </h3>
                  <button
                    onClick={() => setShowMaterials(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                    title="Close Materials"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-3">
                  {studyMaterials.map((material) => {
                    if (!material.url) {
                      return null; // Skip materials without URLs
                    }
                    const isPdfFile = isPDF(material.url);
                    const MaterialWrapper = isPdfFile ? motion.div : motion.a;
                    const wrapperProps = isPdfFile 
                      ? {
                          onClick: (e) => handleMaterialClick(e, material),
                          className: "flex items-center space-x-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors cursor-pointer"
                        }
                      : {
                          href: material.url,
                          target: "_blank",
                          rel: "noopener noreferrer",
                          onClick: (e) => handleMaterialClick(e, material),
                          className: "flex items-center space-x-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors cursor-pointer"
                        };
                    
                    return (
                      <MaterialWrapper
                        key={material.id}
                        {...wrapperProps}
                        whileHover={{ scale: 1.02 }}
                      >
                        <FileText className={`w-5 h-5 flex-shrink-0 ${isPdfFile ? 'text-red-600 dark:text-red-400' : 'text-blue-600 dark:text-blue-400'}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {material.title || material.name || 'Study Material'}
                            </p>
                            {isPdfFile && (
                              <span className="text-xs px-2 py-0.5 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded-full">
                                PDF
                              </span>
                            )}
                          </div>
                          {material.description && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {material.description}
                            </p>
                          )}
                          {material.size && (
                            <p className="text-xs text-gray-400 dark:text-gray-500">
                              {(material.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          )}
                        </div>
                        {isPdfFile ? (
                          <FileText className="w-4 h-4 text-red-400 flex-shrink-0" />
                        ) : (
                          <Download className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        )}
                      </MaterialWrapper>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Inbuilt PDF Viewer */}
      {pdfViewerState.open && pdfViewerState.url && (
        <InbuiltPDFViewer
          url={pdfViewerState.url}
          title={pdfViewerState.title}
          onClose={() => setPdfViewerState({ open: false, url: null, title: null })}
        />
      )}
    </div>
  );
};

export default BatchClassView;

